import * as ExcelJS from "exceljs";

import { FolderNode, UserPerSite } from "../../api-client-nswag/custompermission-client";

const sanitizeForFilename = (value: string): string =>
  value.replace(/[\\/:*?"<>|]/g, "_").trim();

const formatTimestampForFilename = (date: Date): string => {
  const pad2 = (n: number) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const min = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  return `${yyyy}${mm}${dd}_${hh}${min}${ss}`;
};

const flattenFolderTreePreOrder = (roots: FolderNode[] = []): FolderNode[] => {
  const out: FolderNode[] = [];

  const visit = (node: FolderNode) => {
    out.push(node);
    (node.children ?? []).forEach(visit);
  };

  (roots ?? []).forEach(visit);
  return out;
};

export const exportFolderPermissionsToExcel = async (
  folderTree: FolderNode[] = [],
  users: UserPerSite[] = [],
  siteId?: string | number,
  isCsFlow: boolean = false
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Folder Permissions");

  const allUserEmails = (users ?? [])
    .map((u: UserPerSite) => u.userEmail)
    .filter((email: string | undefined): email is string => Boolean(email));

  const userIdToEmailMap = new Map(
    (users ?? []).map((siteUser: UserPerSite) => [
      String(siteUser.userId),
      siteUser.userEmail || String(siteUser.userId),
    ])
  );

  const getUsersCellValue = (node: FolderNode): string => {
    const isRestricted = node?.isRestricted === true;

    if (!isRestricted) {
      return allUserEmails.length ? allUserEmails.join(", ") : "All";
    }

    const ids = (node?.users ?? []).map(String).filter((id: string) => userIdToEmailMap.has(id));
    if (!ids.length) return "";
    return ids.map((id: string) => userIdToEmailMap.get(id) || id).join(", ");
  };

  const headers = [
    "Folder ID",
    "Location",
    "Users",
    "Restricted",
  ];

  if (isCsFlow) {
    headers.push(
      "Scheduled",
      "Published",
      "Date Modified",
      "Last Modified By"
    );
  }

  const headerRow = worksheet.addRow(headers);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  const flattened = flattenFolderTreePreOrder(Array.isArray(folderTree) ? folderTree : []);

  flattened.forEach((node: FolderNode) => {
    const restrictedCell = node?.isRestricted === true ? "Yes" : "No";

    const row = [
      node?.folderId ?? "",
      node?.folderName ?? "",
      getUsersCellValue(node),
      restrictedCell,
    ];

    if (isCsFlow) {
      row.push(
        node?.scheduledPublishDate ?? "",
        node?.publishedDate ?? '',
        node?.dateModified ?? "",
        node?.lastModifiedBy ?? ""
      );
    }

    worksheet.addRow(row);
  });

  worksheet.columns = [
    { width: 14 },
    { width: 50 },
    { width: 80 },
    { width: 14 },
    ...(isCsFlow
      ? [
        { width: 20 },
        { width: 20 },
        { width: 20 },
        { width: 25 },
      ]
      : []),
  ];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");

  const siteIdSuffixRaw =
    siteId !== undefined && siteId !== null && String(siteId).trim() !== ""
      ? String(siteId).trim()
      : "siteid";
  const siteIdSuffix = sanitizeForFilename(siteIdSuffixRaw);
  const timestamp = formatTimestampForFilename(new Date());

  a.href = url;
  a.download = `Custom Permissions Folder Permissions ${siteIdSuffix} ${timestamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
